// ABOUTME: Admin user management component with searchable, sortable, paginated data table
// ABOUTME: Provides user listing, filtering, navigation to detail views, and comprehensive user administration

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Container,
  Button,
  Chip
} from '@mui/material';
import {
  CheckCircle,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  SortingState,
  ColumnFiltersState,
  getPaginationRowModel,
  Column
} from '@tanstack/react-table';
import { useTheme } from '@mui/material/styles';
import * as api from '../services/api';
import { formatRelativeTime } from '../utils/timeUtils';
import SoftDeleteToggle, { SoftDeleteFilter } from './SoftDeleteToggle';

interface UserListItem {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  lastActiveAt: string | null;
  isAdmin: boolean;
  emailVerified: boolean;
  createdAt: string;
  isDeleted: boolean;
}

interface PaginatedUsers {
  items: UserListItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const AdminUserManagement: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [softDeleteFilter, setSoftDeleteFilter] = useState<SoftDeleteFilter>('active');

  const columnHelper = createColumnHelper<UserListItem>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('firstName', {
        header: 'First Name',
        cell: (info) => {
          const user = info.row.original;
          const isDeleted = user.isDeleted;
          const isGreyedOut = softDeleteFilter === 'all' && isDeleted;
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  color: isGreyedOut ? theme.palette.grey[500] : 'inherit',
                  textDecoration: isDeleted && softDeleteFilter === 'all' ? 'line-through' : 'none'
                }}
              >
                {info.getValue()}
              </Box>
              {isDeleted && (
                <Chip 
                  label="DELETED" 
                  size="small" 
                  color="error" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
            </Box>
          );
        },
        meta: {
          filterPlaceholder: 'Search first name...'
        }
      }),
      columnHelper.accessor('lastName', {
        header: 'Last Name',
        cell: (info) => info.getValue(),
        meta: {
          filterPlaceholder: 'Search last name...'
        }
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => info.getValue(),
        meta: {
          filterPlaceholder: 'Search email...'
        }
      }),
      columnHelper.accessor('lastActiveAt', {
        header: 'Last Active',
        cell: (info) => {
          const value = info.getValue();
          return <span>{formatRelativeTime(value)}</span>;
        },
        enableColumnFilter: false
      }),
      columnHelper.accessor('isAdmin', {
        header: 'Admin',
        cell: (info) => {
          const isAdmin = info.getValue();
          return isAdmin ? (
            <CheckCircle data-testid="admin-checkmark" color="primary" />
          ) : (
            <span data-testid="admin-cell-false" />
          );
        },
        enableColumnFilter: false
      })
    ],
    [columnHelper, softDeleteFilter, theme]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    state: {
      pagination,
      sorting,
      columnFilters
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    pageCount: Math.ceil(totalCount / pagination.pageSize)
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const searchParams: Parameters<typeof api.getUsers>[0] & Record<string, string | number | boolean | undefined> = {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize
      };

      // Add soft delete filter
      if (softDeleteFilter === 'deleted') {
        searchParams.showDeleted = 'deleted';
      } else if (softDeleteFilter === 'all') {
        searchParams.showDeleted = 'all';
      }
      // 'active' is the default, no parameter needed

      // Add search filters
      columnFilters.forEach(filter => {
        if (filter.value) {
          searchParams[filter.id] = filter.value as string;
        }
      });

      // Add sorting
      if (sorting.length > 0) {
        const sort = sorting[0];
        searchParams.sortBy = sort.id;
        searchParams.sortDirection = sort.desc ? 'desc' : 'asc';
      }

      const result = await api.getUsers(searchParams) as PaginatedUsers;
      setData(result.items);
      setTotalCount(result.totalCount);
    } catch (err: unknown) {
      setError('Error loading users. Please try again.');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination, sorting, columnFilters, softDeleteFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRowClick = (userId: number) => {
    navigate(`/admin/users/${userId}`);
  };


  const getSortIcon = (column: Column<UserListItem, unknown>) => {
    const sortState = column.getIsSorted();
    if (sortState === 'asc') {
      return <ArrowUpward fontSize="small" />;
    } else if (sortState === 'desc') {
      return <ArrowDownward fontSize="small" />;
    }
    return null;
  };


  if (loading && data.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress data-testid="loading-spinner" />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage system users, permissions, and account settings
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Data Table */}
      <Paper sx={{ p: 3 }}>
        <Box data-testid="user-table-container" sx={{ position: 'relative' }}>
          {/* Soft Delete Filter Toggle */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="h2">
              Users ({totalCount})
            </Typography>
            <SoftDeleteToggle
              value={softDeleteFilter}
              onChange={(newFilter) => {
                setSoftDeleteFilter(newFilter);
                setPagination(prev => ({ ...prev, pageIndex: 0 })); // Reset to first page
              }}
              disabled={loading}
            />
          </Box>

          {/* Search Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {table.getHeaderGroups()[0]?.headers.map((header) => {
              if (!header.column.getCanFilter()) {
                return <Box key={header.id} sx={{ flex: '1 1 180px', minWidth: '150px' }} />;
              }
              return (
                <Box key={header.id} sx={{ flex: '1 1 180px', minWidth: '150px' }}>
                  <TextField
                    size="small"
                    placeholder={(header.column.columnDef.meta as { filterPlaceholder?: string } | undefined)?.filterPlaceholder || 'Search...'}
                    value={(header.column.getFilterValue() as string) ?? ''}
                    onChange={(e) => header.column.setFilterValue(e.target.value)}
                    fullWidth
                  />
                </Box>
              );
            })}
          </Box>

          {/* Table */}
          <Box 
            data-testid="user-table"
            sx={{ 
              overflowX: 'auto',
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'fixed'
              },
              '& th': {
                position: 'relative',
                minWidth: '120px'
              },
              '& th:nth-of-type(1)': { width: '20%' },
              '& th:nth-of-type(2)': { width: '20%' },
              '& th:nth-of-type(3)': { width: '35%' },
              '& th:nth-of-type(4)': { width: '15%' },
              '& th:nth-of-type(5)': { width: '10%' }
            }}
          >
            <Box component="table">
              <Box component="thead" data-testid="table-header" sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Box component="tr" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Box
                        component="th"
                        key={header.id}
                        sx={{
                          padding: '12px',
                          textAlign: 'left',
                          borderBottom: `2px solid ${theme.palette.grey[300]}`,
                          cursor: header.column.getCanSort() ? 'pointer' : 'default'
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                        data-testid={`sort-${header.id}`}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                          <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                          <Box sx={{ width: '24px', height: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                            {getSortIcon(header.column)}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
              <Box component="tbody">
                {!table || table.getRowModel().rows.length === 0 ? (
                  <Box component="tr">
                    <Box component="td" colSpan={columns.length} sx={{ padding: '24px', textAlign: 'center' }}>
                      <Typography color="text.secondary">
                        No users found
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    const isDeleted = row.original.isDeleted;
                    const isGreyedOut = softDeleteFilter === 'all' && isDeleted;

                    return (
                      <Box
                        component="tr"
                        key={row.id}
                        data-testid={`user-row-${row.original.id}`}
                        onClick={() => handleRowClick(row.original.id)}
                        sx={{
                          cursor: 'pointer',
                          borderBottom: `1px solid ${theme.palette.grey[300]}`,
                          opacity: isGreyedOut ? 0.5 : 1,
                          color: isGreyedOut ? theme.palette.grey[500] : 'inherit'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = theme.palette.grey[100];
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <Box
                            component="td"
                            key={cell.id}
                            sx={{
                              padding: '12px',
                              color: isGreyedOut ? theme.palette.grey[500] : 'inherit'
                            }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Box>
                        ))}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>

          {/* Pagination */}
          <Box sx={{ 
            mt: 3, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2">
                Rows per page:
              </Typography>
              <FormControl size="small">
                <Select
                  value={pagination.pageSize}
                  onChange={(e) => setPagination(prev => ({ ...prev, pageSize: Number(e.target.value) }))}
                  data-testid="page-size-select"
                  aria-label="Rows per page:"
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Typography variant="body2">
              Page {pagination.pageIndex + 1} of {Math.ceil(totalCount / pagination.pageSize)}
            </Typography>

            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex - 1 }))}
                disabled={pagination.pageIndex === 0}
                data-testid="pagination-previous"
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPagination(prev => ({ ...prev, pageIndex: prev.pageIndex + 1 }))}
                disabled={pagination.pageIndex >= Math.ceil(totalCount / pagination.pageSize) - 1}
                data-testid="pagination-next"
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminUserManagement;